import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  CloudArrowDown,
  CheckCircle,
  XCircle,
  Lightning,
  Clock,
  ChartLineUp,
  Database,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useConfirm } from "@/components/ConfirmDialog";

export default function DataSync() {
  const confirm = useConfirm();
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [testing, setTesting] = useState({ mixpanel: false, grafana: false });
  const [testResults, setTestResults] = useState({});

  const loadStatus = async () => {
    const [s, l] = await Promise.all([
      api.get("/admin/integrations/status"),
      api.get("/admin/etl/log", { params: { limit: 10 } }),
    ]);
    setStatus(s.data);
    setLogs(l.data.runs || []);
  };

  useEffect(() => {
    loadStatus();
  }, []);

  const test = async (which) => {
    setTesting((t) => ({ ...t, [which]: true }));
    try {
      const r = await api.post(`/admin/integrations/test/${which}`);
      setTestResults((p) => ({ ...p, [which]: r.data }));
      if (r.data.ok) toast.success(`${which}: ${r.data.message || "connected"}`);
      else toast.error(`${which}: ${r.data.error}`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      setTestResults((p) => ({ ...p, [which]: { ok: false, error: msg } }));
      toast.error(`${which}: ${msg}`);
    } finally {
      setTesting((t) => ({ ...t, [which]: false }));
    }
  };

  const runEtl = async () => {
    setRunning(true);
    setLastResult(null);
    try {
      const r = await api.post("/admin/etl/run");
      if (r.data.queued) {
        toast.success("ETL started — auto-refreshing every 5s");
      } else if (r.data.running) {
        toast.info("ETL already running");
      }
      loadStatus();
    } catch (e) {
      setRunning(false);
      toast.error(e.response?.data?.detail || "ETL failed");
    }
  };

  const [repairing, setRepairing] = useState(false);
  const repairFirmUrls = async () => {
    setRepairing(true);
    try {
      const r = await api.post("/admin/firms/backfill-firm-urls");
      const n = r.data.fixed_count;
      if (n === 0) {
        toast.success("All firm URLs are already healthy — nothing to repair.");
      } else {
        toast.success(`Repaired firm_url for ${n} firm(s). Now click Run ETL to pull their Grafana data.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Repair failed");
    } finally {
      setRepairing(false);
    }
  };

  const [importingRenewals, setImportingRenewals] = useState(false);
  const importTeamRenewals = async () => {
    if (!await confirm({
      title: "Import team-finalized renewal dates?",
      body: "217 firms from the uploaded Excel will be loaded.\n\nSafe to run multiple times — re-running just refreshes the same values.",
      confirmLabel: "Import dates",
    })) return;
    setImportingRenewals(true);
    try {
      const r = await api.post("/admin/firms/import-team-renewal-dates");
      const s = r.data.summary;
      toast.success(`Renewal dates imported: ${s.matched} matched · ${s.unmatched} unmatched of ${s.total_in_seed} total`);
      if (r.data.unmatched.length) {
        console.warn("Unmatched firms (need manual fix in Admin → Firms):", r.data.unmatched);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Renewal import failed");
    } finally {
      setImportingRenewals(false);
    }
  };

  const [importingContacts, setImportingContacts] = useState(false);
  const [unmatchedNames, setUnmatchedNames] = useState([]);
  const importContacts = async () => {
    if (!await confirm({
      title: "Import firm contacts?",
      body: "192 firms · 369 contacts from the customer Excel.\n\nSafe to run multiple times — manually-added contacts are NEVER touched; only the imported set is refreshed each run.",
      confirmLabel: "Import contacts",
    })) return;
    setImportingContacts(true);
    try {
      const r = await api.post("/admin/firms/import-contacts");
      const s = r.data.summary;
      toast.success(`Contacts imported: ${s.contacts_inserted} contacts across ${s.matched_firms} firms · ${s.unmatched_firms} unmatched`);
      setUnmatchedNames(r.data.unmatched || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Contact import failed");
    } finally {
      setImportingContacts(false);
    }
  };

  if (!status) {
    return <div className="text-sm text-gray-400 py-12 text-center">Loading…</div>;
  }

  return (
    <div className="space-y-6" data-testid="data-sync-tab">
      {/* Header card */}
      <div className="bg-white border border-gray-200 rounded-lg p-5 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-semibold text-gray-900 flex items-center gap-2">
            <CloudArrowDown size={22} className="text-gray-700" />
            Data Sync
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Pulls activity from Mixpanel and metrics from Grafana, recomputes risk states.
            <span className="ml-1 inline-flex items-center gap-1 text-gray-700">
              <Clock size={13} />
              Auto-runs daily at {status.scheduler.cron} {status.scheduler.timezone}.
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            onClick={runEtl}
            disabled={running}
            className="bg-gray-900 hover:bg-gray-800 text-white"
            data-testid="run-etl-btn"
          >
            <Lightning size={16} className="mr-1.5" weight="fill" />
            {running ? "Running ETL…" : "Run ETL now"}
          </Button>
        </div>
      </div>

      {/* Unmatched names from latest import (admin resolves to existing firm) */}
      {unmatchedNames.length > 0 && (
        <UnmatchedNamesPanel
          names={unmatchedNames}
          onAllResolved={() => setUnmatchedNames([])}
        />
      )}

      {/* Integration cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <IntegrationCard
          title="Mixpanel"
          icon={<ChartLineUp size={20} className="text-gray-700" />}
          configured={status.mixpanel.configured}
          subtitle={`Project ${status.mixpanel.project_id || "—"} · region ${status.mixpanel.region}`}
          testing={testing.mixpanel}
          result={testResults.mixpanel}
          onTest={() => test("mixpanel")}
          docsKey="mixpanel"
          testid="mixpanel-card"
        />
        <IntegrationCard
          title="Grafana"
          icon={<Database size={20} className="text-gray-700" />}
          configured={status.grafana.configured}
          subtitle={status.grafana.base_url || "Not configured"}
          testing={testing.grafana}
          result={testResults.grafana}
          onTest={() => test("grafana")}
          extra={
            <div className="text-[11px] text-gray-500 mt-2">
              Datasource UID: {status.grafana.datasource_uid || "—"} ·{" "}
              SQL template: {status.grafana.metrics_sql_set ? "set" : "not set"}
            </div>
          }
          docsKey="grafana"
          testid="grafana-card"
        />
      </div>

      {/* Last run summary */}
      {lastResult && (
        <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid="last-etl-result">
          <h3 className="font-display text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
            Last run
          </h3>
          <RunSummary run={lastResult} />
        </div>
      )}

      {/* History */}
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid="etl-log">
        <div className="px-5 py-3 border-b border-gray-100 font-display text-sm font-semibold text-gray-700 uppercase tracking-wider">
          Run history
        </div>
        {logs.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-gray-400">
            No ETL runs yet. Configure Mixpanel + Grafana and click "Run ETL now".
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr className="text-left">
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Started
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Duration
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Mixpanel
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Grafana
                </th>
                <th className="px-4 py-2 text-[11px] uppercase tracking-wider font-medium text-gray-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody>
              {logs.map((r, i) => (
                <tr key={r.started_at || `etl-run-${i}`} className="border-b border-gray-100 last:border-b-0">
                  <td className="px-4 py-2 font-mono text-xs text-gray-700">
                    {r.started_at?.slice(0, 19).replace("T", " ")}
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">{r.duration_sec ?? "—"}s</td>
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {r.mixpanel?.firms_updated ?? 0} firms · {r.mixpanel?.events ?? 0} events
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-700">
                    {(r.grafana?.usage_firms_updated ?? r.grafana?.firms_updated ?? 0)} firms
                  </td>
                  <td className="px-4 py-2">
                    {r.ok ? (
                      <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                        <CheckCircle size={13} weight="fill" /> ok
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-xs text-red-700">
                        <XCircle size={13} weight="fill" /> {(r.errors || []).length} err
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Setup help */}
      <SetupHelp />
    </div>
  );
}

function IntegrationCard({ title, icon, configured, subtitle, testing, result, onTest, extra, testid }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5" data-testid={testid}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          {icon}
          <div>
            <div className="font-display font-semibold text-gray-900">{title}</div>
            <div className="text-xs text-gray-500 mt-0.5">{subtitle}</div>
          </div>
        </div>
        {configured ? (
          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
            Configured
          </Badge>
        ) : (
          <Badge className="bg-gray-100 text-gray-600 border-gray-200">Not configured</Badge>
        )}
      </div>
      {extra}
      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="outline"
          size="sm"
          disabled={testing}
          onClick={onTest}
          data-testid={`test-${testid}`}
        >
          {testing ? "Testing…" : "Test connection"}
        </Button>
        {result && (
          <span
            className={`text-xs font-medium ${result.ok ? "text-emerald-700" : "text-red-700"}`}
          >
            {result.ok ? `✓ ${result.message || "ok"}` : `✗ ${result.error}`}
          </span>
        )}
      </div>
    </div>
  );
}

function RunSummary({ run }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
      <Stat label="Mixpanel" value={`${run.mixpanel?.firms_updated ?? 0} firms · ${run.mixpanel?.events ?? 0} events`} ok={run.mixpanel?.configured} />
      <Stat label="Grafana" value={`${run.grafana?.firms_updated ?? 0} firms`} ok={run.grafana?.configured} />
      <Stat label="Duration" value={`${run.duration_sec ?? "—"}s`} ok={run.ok} />
      {run.errors?.length > 0 && (
        <div className="sm:col-span-3">
          <div className="text-[11px] uppercase tracking-wider text-red-700 font-medium mb-1">
            Errors
          </div>
          <pre className="text-xs bg-red-50 border border-red-200 rounded-md p-3 text-red-800 whitespace-pre-wrap">
            {run.errors.join("\n")}
          </pre>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, ok }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wider text-gray-500 font-medium">{label}</div>
      <div className="mt-1 text-base font-semibold text-gray-900 flex items-center gap-1.5">
        {ok ? (
          <CheckCircle size={14} weight="fill" className="text-emerald-500" />
        ) : (
          <XCircle size={14} weight="fill" className="text-gray-400" />
        )}
        {value}
      </div>
    </div>
  );
}

function SetupHelp() {
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-5">
      <h3 className="font-display text-sm font-semibold text-gray-700 uppercase tracking-wider mb-3">
        How to get the credentials
      </h3>
      <div className="space-y-4 text-sm text-gray-700">
        <div>
          <div className="font-medium text-gray-900 mb-1">Mixpanel Service Account</div>
          <ol className="list-decimal pl-5 space-y-1 text-gray-600">
            <li>Go to Mixpanel → top-right gear icon → Organization Settings → Service Accounts.</li>
            <li>Click <span className="font-mono">+ Add Service Account</span> · name it <span className="font-mono">cs-dashboard-etl</span> · role <span className="font-mono">Consumer</span> (read-only).</li>
            <li>Copy the <span className="font-mono">username</span> and <span className="font-mono">secret</span>. Paste into <span className="font-mono">backend/.env</span>:</li>
          </ol>
          <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 font-mono text-gray-800">
{`MIXPANEL_PROJECT_ID="2782469"
MIXPANEL_SERVICE_ACCOUNT_USERNAME="..."
MIXPANEL_SERVICE_ACCOUNT_SECRET="..."
MIXPANEL_REGION="us"  # or "eu" / "in"`}
          </pre>
        </div>
        <div>
          <div className="font-medium text-gray-900 mb-1">Grafana Service Account Token</div>
          <ol className="list-decimal pl-5 space-y-1 text-gray-600">
            <li>Grafana → Administration → Users and Access → Service Accounts → <span className="font-mono">+ Add service account</span>.</li>
            <li>Name <span className="font-mono">cs-dashboard-etl</span> · role <span className="font-mono">Viewer</span>.</li>
            <li>Inside the service account, click <span className="font-mono">+ Add token</span>, copy the <span className="font-mono">glsa_…</span> token.</li>
            <li>Find the datasource UID: Connections → Data sources → click yours → URL contains the UID.</li>
          </ol>
          <pre className="mt-2 text-xs bg-gray-50 border border-gray-200 rounded p-3 font-mono text-gray-800">
{`GRAFANA_BASE_URL="https://grafana.assuredev.in"
GRAFANA_API_TOKEN="glsa_..."
GRAFANA_DATASOURCE_UID="<uid>"
GRAFANA_METRICS_SQL="SELECT subdomain, total_clients, total_assignments
                     FROM firm_metrics_view WHERE subdomain IN ({subdomains})"`}
          </pre>
        </div>
        <div className="text-xs text-gray-500 italic">
          After saving the .env, click <span className="font-medium text-gray-700">Test connection</span> on each card. When both pass, click <span className="font-medium text-gray-700">Run ETL now</span> to refresh activity + metrics.
        </div>
      </div>
    </div>
  );
}


function UnmatchedNamesPanel({ names, onAllResolved }) {
  const [firms, setFirms] = useState([]);
  const [pending, setPending] = useState(names);
  const [search, setSearch] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/firms", { params: { page_size: 500, status: "all" } }).then((r) => {
      setFirms(r.data.firms || []);
    });
  }, []);

  useEffect(() => { setPending(names); setActiveIdx(0); setSearch(""); }, [names]);

  const current = pending[activeIdx];
  if (!current) return null;

  const filtered = firms.filter((f) => {
    const q = search.toLowerCase();
    if (!q) return true;
    return (
      f.firm_name?.toLowerCase().includes(q) ||
      f.subdomain?.toLowerCase().includes(q) ||
      f.city?.toLowerCase().includes(q)
    );
  }).slice(0, 25);

  const assign = async (firm) => {
    setSaving(true);
    try {
      // Append the unmatched name as an alias on the chosen firm.
      // Get current aliases first (the GET /firms/{sub} returns full firm shape).
      const existing = await api.get(`/firms/${firm.subdomain}`);
      const aliases = Array.from(new Set([...(existing.data.import_aliases || []), current.firm_name.trim()]));
      await api.put(`/firms/${firm.subdomain}`, { import_aliases: aliases });
      toast.success(`Linked "${current.firm_name}" → ${firm.firm_name}`);
      const next = pending.filter((_, i) => i !== activeIdx);
      setPending(next);
      setActiveIdx(0);
      setSearch("");
      if (next.length === 0) {
        onAllResolved();
        toast.success("All unmatched names resolved — click 'Import contacts' again to pull them in.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const skip = () => {
    setActiveIdx((i) => Math.min(i + 1, pending.length - 1));
    setSearch("");
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3" data-testid="unmatched-names-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-semibold text-amber-900 text-sm">
            {pending.length} unmatched name{pending.length === 1 ? "" : "s"} from the import
          </div>
          <div className="text-xs text-amber-700 mt-0.5">
            Pick the firm each one refers to — we'll save it as an alias so future imports match automatically.
          </div>
        </div>
        <button
          onClick={() => onAllResolved()}
          className="text-xs text-amber-700 hover:text-amber-900 underline"
        >
          Dismiss
        </button>
      </div>

      <div className="bg-white border border-amber-200 rounded-md p-3 space-y-3">
        <div className="text-xs text-gray-500">
          Resolving {activeIdx + 1} of {pending.length}
        </div>
        <div className="font-display text-base font-semibold text-gray-900">
          “{current.firm_name}”
          {current.contact_count !== undefined && (
            <span className="ml-2 text-xs font-mono text-gray-500">({current.contact_count} contacts)</span>
          )}
        </div>
        <input
          autoFocus
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search existing firm by name, subdomain or city…"
          className="w-full text-sm rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-400/30"
          data-testid="unmatched-search"
        />
        <div className="max-h-72 overflow-y-auto border border-gray-100 rounded-md">
          {filtered.length === 0 && (
            <div className="px-3 py-6 text-center text-xs text-gray-400">No firms match your search.</div>
          )}
          {filtered.map((f) => (
            <button
              key={f.subdomain}
              disabled={saving}
              onClick={() => assign(f)}
              className="w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 hover:bg-amber-50 flex items-center justify-between gap-3 disabled:opacity-50"
              data-testid={`unmatched-pick-${f.subdomain}`}
            >
              <div>
                <div className="text-sm font-medium text-gray-900">{f.firm_name}</div>
                <div className="text-[11px] text-gray-500 font-mono">{f.subdomain} · {f.city} · {f.tier}</div>
              </div>
              <span className="text-[10px] uppercase tracking-wider text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
                Link
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between pt-1">
          <button onClick={skip} className="text-xs text-gray-500 hover:text-gray-900 underline">
            Skip this one
          </button>
          <div className="text-[10px] text-gray-400">
            {pending.length - activeIdx - 1} remaining after this
          </div>
        </div>
      </div>
    </div>
  );
}
